/* eslint-disable react-refresh/only-export-components */
import { createContext, Dispatch, FC, Reducer, useReducer } from 'react';

import { areCookiesAccepted, areCookiesRejected } from 'app/api/cookie';
import { PropsWithOnlyChildren } from 'app/types';
import { ParsedToken, parseJWT } from 'app/utils/lib/string';

export interface State {
  areCookiesAllowed: boolean | null;
  authRoles: number[];
  companyId: null | string;
  tokenData: null | ParsedToken;
}

interface ResetAction {
  type: 'reset';
}
interface SetLoginInfoAction {
  payload: {
    token: string;
  };
  type: 'setLoginInfo';
}
interface UpdateCookiePermission {
  payload: {
    areCookiesAllowed: boolean;
  };
  type: 'updateCookiePermission';
}

export type AuthContextAction =
  | ResetAction
  | SetLoginInfoAction
  | UpdateCookiePermission;

type AuthDispatch = Dispatch<AuthContextAction>;

// Local Variables
function getAreCookiesAllowed() {
  if (areCookiesAccepted()) {
    return true;
  }

  if (areCookiesRejected()) {
    return false;
  }

  return null;
}

const initialState: State = {
  areCookiesAllowed: getAreCookiesAllowed(),
  authRoles: [],
  companyId: null,
  tokenData: null,
};

export const AuthStateContext = createContext<State>(initialState);
export const AuthDispatchContext = createContext<AuthDispatch>(undefined as unknown as AuthDispatch);

AuthStateContext.displayName = 'AuthState';
AuthDispatchContext.displayName = 'AuthDispatch';

const reducer: Reducer<State, AuthContextAction> = (state, action) => {
  switch (action.type) {
    case 'reset':
      return { ...initialState };
    case 'setLoginInfo': {
      const { token } = action.payload;
      const parsedToken = token ? parseJWT(token) : null;
      const authRoles = parsedToken?.Roles.split(',').map(roleId => Number(roleId)) ?? [];
      const companyId = parsedToken?.CId ?? '';

      return {
        ...state,
        authRoles,
        companyId,
        tokenData: parsedToken,
      };
    }
    case 'updateCookiePermission':
      return {
        ...state,
        areCookiesAllowed: action.payload.areCookiesAllowed,
      };

    default:
      throw new Error();
  }
};

const AuthContextProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>
        {children}
      </AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export default AuthContextProvider;
